import { Route, Switch } from 'react-router-dom';
import InventoryPicker from '../components/contingent/inventoryPicker';
import Loading from '../components/loading';
import PropTypes from 'prop-types';
import QueryString from 'query-string';
import QuotaSummary from './quotas/quotaSummary';
import QuotasTabular from './quotas/tabular';
import QuotasTabularEg from './quotas/tabulareg';
import QuotaViewer from './quotas/viewer';
import React from 'react';
import Inventory from '../models/inventory';
import ZoneModel from '../models/zoneModel';
import QuotasModel from '../models/quotasModel';
import QuotasSummaryModel from '../models/quotasSummaryModel';
import ContingentModel from '../models/contingentModel';
import RateModel from '../models/rateModel';
import useAppContext from '../context/useAppContext';
import PageError from './error/pageError';

const uuidRegex = '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

export default function Quotas({ inventoryId, history, match }) {
    const [inventory, setInventory] = React.useState(null);
    const [zones, setZones] = React.useState(null);
    const [contingents, setContingents] = React.useState(null);
    const [rates, setRates] = React.useState(null);
    const [{ event, organization }] = useAppContext();
    const [editMode, setEditMode] = React.useState(null);
    const [quotas, setQuotas] = React.useState(null);
    const [quotasSummary, setQuotasSummary] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [reload, setReload] = React.useState(false);

    let organizationId = organization.id;
    let eventId = event.id;

    React.useEffect(() => {
        setLoading(true);
        let inventoryPromise = Inventory.get({
            chartId: event.chart,
            organizationId,
            id: inventoryId
        });
        let zonePromise = ZoneModel.getAll(
            {
                chartId: event.chart,
                organizationId
            },
            `inventories=${inventoryId}`
        );
        let contingentPromise = ContingentModel.getAll({
            eventId,
            organizationId,
            inventoryId
        });

        let ratePromise = RateModel.getAll({
            eventId,
            organizationId
        });

        let quotasPromise = QuotasModel.getAll({
            eventId,
            organizationId,
            inventoryId
        });

        let quotasSummaryPromise = QuotasSummaryModel.getAll({
            eventId,
            organizationId,
            inventoryId
        });

        Promise.all([inventoryPromise, contingentPromise, zonePromise, quotasPromise, quotasSummaryPromise, ratePromise])
            .then(([inventoryResponse, contingentResponse, zoneResponse, quotasResponse, quotasSummaryResponse, rateResponse]) => {
                setInventory(inventoryResponse);
                setContingents(contingentResponse);
                setQuotas(quotasResponse);
                setQuotasSummary(quotasSummaryResponse);
                setZones(zoneResponse);
                setRates(rateResponse);
                setLoading(false);
            })
            .catch(e => {});
        setEditMode(getEditMode());
    }, [inventoryId, reload]);

    /**
     * Get edit mode from  url params
     */
    function getEditMode() {
        if (window.location.search) {
            let query = QueryString.parse(window.location.search);
            if (query.editMode) {
                return true;
            }
        }

        return false;
    }

    function filterRates(rates, contingent, inventory) {
        // set length=1 for now
        return rates.filter(rate => rate.contingents.indexOf(contingent.pk) > -1 && rate.inventories.indexOf(inventory.pk) > -1);
    }

    const changeInventory = React.useCallback(
        inventory => {
            setInventory(inventory);
            setLoading(true);
            history.push(`/ticket/O${organizationId}/E${eventId}/seating/quotas/inventory/${inventory.uuid}`);
        },
        [inventory, organization, event, history]
    );

    const renderInventorySelector = React.useCallback(() => {
        return <InventoryPicker currentInventory={inventory} inventories={event.inventories} onChange={inventory => changeInventory(inventory)} disabled={editMode} />;
    }, [changeInventory, inventory, event, editMode]);

    if (loading || !(zones && rates && inventory && contingents && quotas && quotasSummary)) {
        return <Loading />;
    }
    return (
        <Switch>
            <Route
                exact
                path={`${match.path}`}
                render={props => <QuotaSummary inventoryId={inventoryId} renderInventorySelector={renderInventorySelector} quotas={quotasSummary} />}
            />
            <Route
                path={`${match.path}/contingent/:contingentId(${uuidRegex})`}
                render={routeProps => {
                    let {
                        match: {
                            params: { contingentId }
                        }
                    } = routeProps;
                    let contingent = contingents.find(x => x.pk === contingentId);
                    if (!contingent) {
                        // 404
                        return <PageError errorCode={404} backHomeUrl={`/ticket/O${organization.id}/E${event.id}/dashboard`} />;
                    }

                    return (
                        <Switch>
                            <Route
                                exact
                                path={`${routeProps.match.path}/edit`}
                                render={routeProps => {
                                    let tmpRates = filterRates(rates, contingent, inventory);
                                    return (
                                        <QuotaViewer
                                            editMode={editMode}
                                            contingent={contingent}
                                            currentInventory={inventory}
                                            quotas={quotas}
                                            rates={tmpRates}
                                            renderInventorySelector={renderInventorySelector}
                                            zones={zones}
                                            history={routeProps.history}
                                            reload={() => setReload(!reload)}
                                        />
                                    );
                                }}
                            />
                            <Route
                                exact
                                path={`${routeProps.match.path}/tabular`}
                                render={routeProps => {
                                    return (
                                        <QuotasTabular
                                            contingent={contingent}
                                            currentInventory={inventory}
                                            rates={rates}
                                            quotas={quotas}
                                            renderInventorySelector={renderInventorySelector}
                                            zones={ZoneModel.sortByName(zones)}
                                            history={routeProps.history}
                                        />
                                    );
                                }}
                            />
                        </Switch>
                    );
                }}
            />
            <Route
                path={`${match.path}/generaladmission/:generalAdmissionId`}
                render={routeProps => {
                    let {
                        match: {
                            params: { generalAdmissionId }
                        }
                    } = routeProps;
                    return (
                        <Switch>
                            <Route
                                exact
                                path={`${routeProps.match.path}/tabulareg`}
                                render={routeProps => {
                                    return (
                                        <QuotasTabularEg
                                            currentInventory={inventory}
                                            generalAdmissionId={+generalAdmissionId}
                                            quotas={quotas}
                                            quotasSummary={quotasSummary}
                                            rates={rates}
                                            renderInventorySelector={renderInventorySelector}
                                            zones={zones}
                                            history={routeProps.history}
                                        />
                                    );
                                }}
                            />

                            <Route
                                exact
                                path={`${routeProps.match.path}/edit`}
                                render={routeProps => {
                                    return (
                                        <QuotaViewer
                                            editMode={editMode}
                                            contingent={null}
                                            currentInventory={inventory}
                                            generalAdmissionId={generalAdmissionId}
                                            quotas={quotas}
                                            quotasSummary={quotasSummary}
                                            rates={rates}
                                            renderInventorySelector={renderInventorySelector}
                                            zones={ZoneModel.sortByName(zones)}
                                            history={routeProps.history}
                                            reload={() => setReload(!reload)}
                                        />
                                    );
                                }}
                            />

                            <Route render={() => <PageError errorCode={404} backHomeUrl={`/ticket/O${organization.id}/E${event.id}/dashboard`} />} />
                        </Switch>
                    );
                }}
            />
            <Route render={() => <PageError errorCode={404} backHomeUrl={`/ticket/O${organization.id}/E${event.id}/dashboard`} />} />
        </Switch>
    );
}

Quotas.propTypes = {
    inventoryId: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired
};
